import React, {useEffect, useState} from "react";
import {useHistory} from "react-router";
import TopBarRedesigned from "components/TopBarRedesigned/TopBarRedesigned";
import {exitReact, getPaymentTerms} from "utils";
import { HygienistProps, InvoiceProps, JobProps, DateString, TimeString } from "utils/types";
import { BasicContainer, StyledCheckboxInput, AddressSection, DateSection, BillingSection, BillingRow, SectionTitle, PaymentInfo, PaymentSection, PaymentNote, PayNowButton, AlreadyPaidButton } from "./InvoicePage.styles";
import dayjs from "dayjs";
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";
import {Analytics} from "utils/analytics";
import FixedLoader from "../../../components/FixedLoader";
import {SimpleModal} from "../../../components/Modal";
import {InvoiceAlreadyPaidModal} from "./components/AlreadyPaidModal";
import {PayInvoiceModal} from "./components/PayInvoiceModal";
import {isEmpty} from "lodash";
import { ErrorModal } from "./components/ErrorModal";
import { CalendarTodayOutlined, AccessTimeOutlined, PaymentOutlined, HelpOutlineOutlined } from "@mui/icons-material";


const hourFormat = (h: number) => {
    const totalMinutes =  h * 60;
    let hours = totalMinutes / 60 | 0;
    let minutes = totalMinutes % 60 | 0;
    if (hours === 0 && minutes === 0) {
        return '0';
    }
    return ((hours < 0) ? '0' : hours) + ':' + ((minutes <= 0) ? '00' : minutes);
}

const convertDentalOfficeStatus = (status: number) => {
    if (status === 1) {
        return 'Pro'
    }
    if (status === 2) {
        return 'Elite'
    }
    return 'Active'
}

export interface JobWithInvoiceProps extends JobProps {
    hygienist: HygienistProps,
    invoice: InvoiceProps
}

// @ts-ignore
const app = window?.app as { hidePreloader: () => void } | undefined;

const InvoicePage = () => {
    app?.hidePreloader();
    const history = useHistory();
    const { jobId, job: jobData } = history.location.state as { jobId: number, job?: JobWithInvoiceProps }
    const [loading, setLoading] = useState(true)

    const [checked, setChecked] = useState(false)
    const [job, setJob] = useState<JobWithInvoiceProps | undefined>(jobData)
    const [error, setError] = useState<string>('')
    const [errorTitle, setErrorTitle] = useState<string>('Error')
    const [alreadyPaidModal, setAlreadyPaidModal] = useState<boolean>(false)
    const [alreadyPaid, setAlreadyPaid] = useState<boolean>(false)
    const [payInvoiceModal, setPayInvoiceModal] = useState<boolean>(false)

    const FEE_PERCENTAGE = 4.75;
    const invoiceAmount = !job ? 0
        : job.invoice.totalInvoiceAmt;

    const fee = !job ? 0
        : invoiceAmount * (FEE_PERCENTAGE / 100);

    const invoiceAmtWithFee = (invoiceAmount + fee).toFixed(2)

    const goHome = () => {
        history.push('loading')
        exitReact('home')
    }

    const [getInvoiceForJobLoading, , getInvoiceForJob] = useActionApi({
        apiMethod: Api.getInvoiceForJob,
        onSuccess: (invoice: InvoiceProps) => {
            if (!job) {
                return;
            }
            if (!invoice.dentistMarkedPaid) {
                payInvoice(job.invoice.id);
                return;
            }
            setAlreadyPaid(true);
            setErrorTitle('Invoice Already Paid');
            setError('This invoice was already paid using Pay Now');
        },
        onError: (e: any) => {
            setLoading(false);
            setErrorTitle('Failed to load invoice');
            setError('Could not retrieve invoice details. Please try again later.');
        }
    });

    const getData = async () => {
        try {
            const fetchedJob = await Api.getJobDetails(jobId);
            const invoice = await Api.getInvoiceForJob(jobId);
            setJob({invoice, ...fetchedJob})
        } catch (e) {
            setErrorTitle('Failed to load job details');
            setError('Could not retrieve job details. Please try again later.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if(!isEmpty(jobData)) {
            setJob(jobData);
            setAlreadyPaid(!!jobData?.invoice?.dentistMarkedPaid);
            setLoading(false);
            return;
        }
        if(jobId) {
            setLoading(true)
            getData().then(() => setLoading(false))
            return;
        }
        setLoading(false)
    }, [])

    const [payInvoiceLoading, , payInvoice] = useActionApi({
        apiMethod: Api.payInvoice,
        onSuccess: () => {
            if(!job) {
                return;
            }
            setAlreadyPaid(true)
            Analytics.track('pay invoice', {
                shiftStartDate: new Date(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].actualStart).toISOString(),
                shiftEndDate: new Date(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].actualEnd).toISOString(),
                shiftId: job.shifts[0].id,
                totalInvoiceAmount: job.invoice.totalInvoiceAmt,
                currencyCode: job.location.country ? 'USD' : 'CAD',
                dentalWorkerStatus: convertDentalOfficeStatus(job.location?.status ? job.location?.status : 0),
                dentalWorkerType: job.hygienist.type ? 'Assistant' : 'Hygienist',
                hasUploadedResume: !!(job.hygienist
                    && job.hygienist.resumeUrl
                    && job.hygienist.resumeUrl.length),
                graduationYear: job.hygienist.graduationYear + '-01-01',
                dentalOfficeLocationId: job.locationId,
                shiftPostDate: new Date(job.postedOn).toISOString(),
            });
            goHome()
        },
        onError: (e: any) => {
            setErrorTitle('Error')
            setError(`Error processing payment: ${JSON.parse(e.message).message}`)
        }
    });

    const payInvoiceHandler = (e: any) => {
        e.preventDefault()
        if (!checked) {
            setErrorTitle('Cannot Proceed')
            setError('Please agree to Pay Now payment authorization to process payment.')
        } else {
            setPayInvoiceModal(true)
        }
    }

    const alreadyPaidHandler = (e: any) => {
        e.preventDefault()
        setAlreadyPaidModal(true)
    }

    const onGoPaymentInfoPageClick = (e: any) => {
        setAlreadyPaidModal(false)
        setError('')
        if (job) {
            exitReact('payment-info', { locationId: job.locationId })
        }
    }

    function toDayJS(date?: DateString, time?: TimeString | null): dayjs.Dayjs | null {
        if (!date || !time) {
            return null;
        }
        return dayjs(date + ' ' + time, 'YYYY-MM-DD h:mm a');
    }

    const shift = job?.shifts?.at(0);
    const startTime = toDayJS(job?.startDate, shift?.actualStart)?.format("h:mma")
    const endTime = toDayJS(job?.startDate, shift?.actualEnd)?.format("h:mma")

    if (loading) {
        return <FixedLoader loading={true} />;
    }

    if (error) {
        return (
            <BasicContainer>
                <SimpleModal isOpen={true}>
                    <ErrorModal
                        message={error}
                        title={errorTitle}
                        onCloseButtonClick={goHome}
                        onGoPaymentInfoPageClick={onGoPaymentInfoPageClick}
                    />
                </SimpleModal>
            </BasicContainer>
        );
    }

    if (!job) {
        return <></>
    }

    return (
        <>
            <TopBarRedesigned
                onBack={goHome}
                title={'Invoice Details'}
            />

            <BasicContainer>
                <SimpleModal isOpen={!!error.length}>
                    <ErrorModal
                        message={error}
                        title={errorTitle}
                        onCloseButtonClick={() => setError('')}
                        onGoPaymentInfoPageClick={onGoPaymentInfoPageClick}
                    />
                </SimpleModal>

                <SimpleModal isOpen={alreadyPaidModal} >
                    <InvoiceAlreadyPaidModal
                        job={job}
                        onCloseButtonClick={() => setAlreadyPaidModal(false)}
                        onPaid={() => {
                            setAlreadyPaid(true)
                            setAlreadyPaidModal(false)
                            goHome()
                        }}
                    />
                </SimpleModal>

                <SimpleModal isOpen={payInvoiceModal} >
                    <PayInvoiceModal
                        job={job}
                        onPayInvoice={() => {
                            setPayInvoiceModal(false)
                            getInvoiceForJob(job.id)
                        }}
                        onCloseButtonClick={() => setPayInvoiceModal(false)}
                    />
                </SimpleModal>

                <AddressSection>
                    <div>
                        <SectionTitle className="title">{job.hygienist.firstName} {job.hygienist.lastName}</SectionTitle>
                        <div className="subtitle">{job.hygienist.address}</div>
                        <div className="subtitle">{job.hygienist.city}, {job.hygienist.province}</div>
                        <div className="subtitle">{job.hygienist.postalCode}</div>
                    </div>
                    <div>
                        <SectionTitle className="title">Invoice #{job.id}</SectionTitle>
                        <div className="subtitle">{dayjs(job.invoice.sentOn).format('dddd, MMM D')}</div>
                    </div>
                </AddressSection>

                <DateSection>
                    <div className="calendar-date">
                        <CalendarTodayOutlined fontSize="medium"/>
                        {dayjs(job.startDate).format('dddd, MMM D')}
                    </div>
                    <div>{startTime} - {endTime}</div>
                </DateSection>

                <BillingSection>
                    <SectionTitle>
                        <AccessTimeOutlined fontSize="medium"/> Time & Billing Details
                    </SectionTitle>
                    
                    <BillingRow>
                        <div>Total Time</div>
                        <div>{hourFormat(job.invoice.totalHours)}hrs</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Unpaid Time</div>
                        <div>{hourFormat(job.invoice.totalUnpaidHours)}hrs</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Billable Time</div>
                        <div>{hourFormat(job.invoice?.totalBillableHours ?? 0)}hrs</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Hourly Rate</div>
                        <div>${job.invoice.hourlyRate}/hr</div>
                    </BillingRow>
                    <BillingRow>
                        <div>Travel Allowance</div>
                        <div>${job.invoice?.travelAllowance?.toFixed(2) ?? 0}</div>
                    </BillingRow>
                    <BillingRow className="grand-total">
                        <div>Grand Total</div>
                        <div>${job.invoice.totalInvoiceAmt}</div>
                    </BillingRow>
                </BillingSection>

                <PaymentSection>
                    <SectionTitle>
                        <PaymentOutlined fontSize="medium" /> Payment Information
                    </SectionTitle>
                    <PaymentInfo>
                        <div>Payment Terms:</div>
                        <div>You agreed to pay {job.hygienist.firstName} <span className="payment-terms">{getPaymentTerms(job?.paymentTerms)}</span></div>
                    </PaymentInfo>
                    <PaymentInfo>
                        <div>Expected Payment Date:</div>
                        <div>{dayjs(job.invoice.sentOn).add(job?.paymentTerms, 'day').format('dddd, MMM D')}</div>
                    </PaymentInfo>
                </PaymentSection>

                {alreadyPaid ? (
                    <h1 style={{textAlign: 'center', margin: '2rem'}}>Already Paid!</h1>
                ) : (
                    <>
                        <PaymentNote>
                            Most offices use TempStars' Pay Now to pay their invoices - it's fast & easy, but optional. If you have a preferred way to pay temp workers, that's fine. When you use Pay Now, we'll forward {job.hygienist.firstName}'s payment to them within 24hrs of receiving it.
                        </PaymentNote>

                        <StyledCheckboxInput
                            name={'payment'}
                            label={`I authorize TempStars to process $${invoiceAmount} + ${FEE_PERCENTAGE}% payment processing fee to my credit card, to settle ${job.hygienist.firstName}'s invoice for services provided on ${dayjs(job.startDate).format('dddd, MMM D')}, for a total of $${invoiceAmtWithFee}. As a convenient payment intermediary, TempStars will forward $${invoiceAmount} to ${job.hygienist.firstName} ${job.hygienist.lastName} on your behalf, and retain the 4.75% fee to cover our transaction costs.`}
                            checked={checked}
                            onChange={() => setChecked(!checked)}
                        />

                        <PayNowButton
                            disabled={alreadyPaidModal || alreadyPaid}
                            onClick={payInvoiceHandler}
                        >
                            Pay Now
                        </PayNowButton>

                        <AlreadyPaidButton
                            onClick={alreadyPaidHandler}
                        >
                            <HelpOutlineOutlined fontSize="small" />
                            Have you already paid this invoice?
                        </AlreadyPaidButton>
                    </>
                )}
            </BasicContainer>
            <FixedLoader loading={getInvoiceForJobLoading || payInvoiceLoading}/>
        </>
    );
};

export default InvoicePage;